import type { LokiStreamEntry, PromMatrixEntry } from '../types';

export const TIME_RANGES = ['1h', '6h', '24h', '7d'] as const;
export type TimeRange = (typeof TIME_RANGES)[number];
const RANGE_SECONDS: Record<TimeRange, number> = { '1h': 3_600, '6h': 21_600, '24h': 86_400, '7d': 604_800 };

export function timeRangeToQueryWindow(range: string, now: number = Date.now()): { start: number; end: number; step: number } {
  const seconds = RANGE_SECONDS[range as TimeRange] ?? RANGE_SECONDS['6h'];
  const end = Math.floor(now / 1000);
  return { start: end - seconds, end, step: Math.max(15, Math.ceil(seconds / 300)) };
}

export function seriesKey(labels: Record<string, string>): string {
  return Object.entries(labels)
    .filter(([k]) => k !== '__name__')
    // serial pins FIRST (legend reads "KIN-A001 · sensor=tsens0"), remaining
    // labels alphabetical — a plain localeCompare would sort 'sensor' before
    // 'serial' and flip the legend order.
    .sort(([a], [b]) => (a === 'serial' ? -1 : b === 'serial' ? 1 : a.localeCompare(b)))
    .map(([k, v]) => (k === 'serial' ? v : `${k}=${v}`))
    .join(' · ');
}

export interface ChartPoint { ts: number; [series: string]: number; }

export function promMatrixToChartData(series: PromMatrixEntry[]): { keys: string[]; data: ChartPoint[] } {
  const byTs = new Map<number, ChartPoint>();
  const keys: string[] = [];
  for (const entry of series) {
    const key = seriesKey(entry.metric);
    keys.push(key);
    for (const [ts, v] of entry.values) {
      const ms = ts * 1000;
      const row = byTs.get(ms) ?? { ts: ms };
      row[key] = Number(v);
      byTs.set(ms, row);
    }
  }
  return { keys, data: [...byTs.values()].sort((a, b) => a.ts - b.ts) };
}

export function reduceMatrix(series: PromMatrixEntry[], mode: 'last' | 'avg' | 'max'): number | null {
  const all: Array<[number, number]> = [];
  for (const s of series) for (const [ts, v] of s.values) {
    const n = Number(v);
    if (Number.isFinite(n)) all.push([ts, n]);
  }
  if (all.length === 0) return null;
  if (mode === 'avg') return all.reduce((acc, [, v]) => acc + v, 0) / all.length;
  if (mode === 'max') return Math.max(...all.map(([, v]) => v));
  let best: [number, number] = all[0];
  for (const cur of all) if (cur[0] >= best[0]) best = cur;
  return best[1];
}

export function gaugeBounds(unit: string, options?: { min?: number; max?: number }): { min: number; max: number } | null {
  const min = options?.min ?? 0;
  if (options?.max !== undefined) return { min, max: options.max };
  if (unit === 'pct') return { min, max: 100 };
  if (unit === 'bool') return { min, max: 1 };
  return null;
}

export interface FaultRow { ts: number; serial: string; line: string; }

export function lokiToFaultRows(events: LokiStreamEntry[]): FaultRow[] {
  const rows: FaultRow[] = [];
  for (const e of events) {
    const serial = e.stream.serial ?? 'unknown';
    for (const [nanos, line] of e.values) {
      rows.push({ ts: Math.floor(Number(nanos) / 1e6), serial, line });
    }
  }
  return rows.sort((a, b) => b.ts - a.ts);
}
